import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MultiSelectButton } from '../../../../global/multi-select/multi-select-button';
import { TEventTypeLabel } from '../../../../../api/events.dao';
import { eventTypes } from '../../../../../constants/EventValues';
import './event-types-multiselect.scss';
import Text from '../../../../global/text/text';
import DefaultButton from '../../../../global/default-button/default-button';
import { REMOVE_TEXT, addText, cancelText } from '../../../../../constants/common-labels.constants';

export type Props = {
    selectedEventTypes: TEventTypeLabel[];
    disabledEventTypes: TEventTypeLabel[];
    setEventTypes: (eventTypes: TEventTypeLabel[]) => void;
    removeEventType: (eventType: TEventTypeLabel) => void;
};

type EventType = { value: TEventTypeLabel };

export const EventTypesMultiselect = observer((props: Props): JSX.Element => {
    const intl = useIntl();

    const { setEventTypes, removeEventType } = props;

    const eventTypeList = eventTypes(intl).map(({ label }) => ({ value: label })) as EventType[];
    const eventTypeLabelToEventType = (value: TEventTypeLabel): EventType => ({ value: value });

    const selectedEventTypes = props.selectedEventTypes.map(eventTypeLabelToEventType);
    const disabledEventTypes = props.disabledEventTypes.map(eventTypeLabelToEventType).concat(selectedEventTypes);

    const onSetEventTypes = (addedEventTypes: EventType[]): void => setEventTypes(addedEventTypes.map(({ value }) => value));

    return (
        <section className='event-types-section m-bottom-20px'>
            <div className='event-type-list'>
                <Text
                    tag='div'
                    className='event-type-list__title'
                    message={intl.formatMessage({id: 'oe.blueprints.eventTypes.label', defaultMessage: 'Event Types', description: 'Label text'})}
                />
                {selectedEventTypes.map(({ value }) => {
                    return (
                        <div className='event-type-list-item' key={value}>
                            <Text
                                tag='div'
                                className='event-type-list-item__text'
                                message={value}
                                withEllipsis
                            />
                            <DefaultButton
                                text={<FormattedMessage {...REMOVE_TEXT} />}
                                modifier='remove'
                                onClick={() => removeEventType(value)}
                            />
                        </div>
                    );
                })}
            </div>
            <MultiSelectButton
                items={eventTypeList}
                disabled={disabledEventTypes}
                value={selectedEventTypes}
                keyProperty='value'
                nameProperty='value'
                text={<FormattedMessage
                    id='oe.eventType.add'
                    description='Button text'
                    defaultMessage='{prefix} Add Event Types'
                    values={{ prefix: '+' }}
                />}
                applyButtonText={addText()}
                resetButtonText={cancelText()}
                onChange={onSetEventTypes}
            />
        </section>
    );
});
